/* istanbul ignore file */
import React from 'react';
import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import styled from '@emotion/styled';

import { useSystemTranslation } from 'hooks';
import { getInstallmentDuration, InstallmentChargeGroup } from 'utils/installment-charges';

import CartItem from './CartItem';
import { InstallmentChargeItem } from './InstallmentChargeItem';

interface Props {
  groups: InstallmentChargeGroup[];
}

export function InstallmentsAccordionSection({ groups }: Props) {
  const { tSystem } = useSystemTranslation();

  const formatDuration = (group: InstallmentChargeGroup) => {
    const { unit, count, remainingDays } = getInstallmentDuration(
      group.startDate,
      group.endDate,
      group.arTriggerId,
    );
    const singularUnitKey =
      unit === 'month' ? SystemTranslationKeys.payment.month : SystemTranslationKeys.payment.week;
    const pluralUnitKey =
      unit === 'month' ? SystemTranslationKeys.payment.months : SystemTranslationKeys.payment.weeks;
    const unitKey = count === 1 ? singularUnitKey : pluralUnitKey;
    const unitText = tSystem(unitKey, { count });
    if (!remainingDays) return unitText;

    const dayText = tSystem(
      remainingDays === 1 ? SystemTranslationKeys.payment.day : SystemTranslationKeys.payment.days,
      { count: remainingDays },
    );
    return `${unitText} + ${dayText}`;
  };

  return (
    <AccItem>
      <h2>
        <AccordionButton>
          <Box flex="1" textAlign="left">
            {tSystem(SystemTranslationKeys.payment.installments)}
          </Box>
          <AccIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel pb={4}>
        {groups.map((group) => (
          <InstallmentGroup key={group.id}>
            <Text fontWeight="semibold">
              {tSystem(SystemTranslationKeys.payment.installment, { number: group.number })}
            </Text>
            <Text color="gray.base" fontSize="sm">
              {formatDuration(group)}
            </Text>
            <Text color="gray.base" fontSize="xs" textAlign="right">
              {tSystem(SystemTranslationKeys.payment.installmentChargeAmounts)}
            </Text>
            {group.charges.map((charge) => (
              <InstallmentChargeItem key={charge.id} charge={charge} />
            ))}
            <CartItem
              label={tSystem(SystemTranslationKeys.payment.installmentTotal)}
              total
              priceNumber={group.total}
              currencyCode={group.currencyCode}
            />
          </InstallmentGroup>
        ))}
      </AccordionPanel>
    </AccItem>
  );
}

const AccItem = styled(AccordionItem)`
  border-top: 1px solid ${({ theme }: any) => theme.colors.gray.light};
`;

const AccIcon = styled(AccordionIcon)`
  background: ${({ theme }: any) => theme.colors.gray.light};
  border-radius: 4px;
  width: 25px;
  height: 25px;
`;

const InstallmentGroup = styled(Box)`
  & + & {
    border-top: 1px solid ${({ theme }: any) => theme.colors.gray.light};
    margin-top: 12px;
    padding-top: 12px;
  }
`;
